/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  FormSelect,
  PrimaryButton,
  SearchInput,
} from "../../components";
import { useAlert } from "../../store/useAlert";
import { useAppLoading } from "../../store/useAppLoading";
import { useUsers } from "../../store/useUsers";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { withSuperAdminPermission } from "../../utils/HOC/withSuperAdminPermission";

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const TTL_DAYS = 14;
const CLOSED_PAGE_SIZE = 20;

// Days left before the Mongo TTL index (user.model.ts) purges the account -
// deletion_date is set the moment pending_delete becomes true.
const daysRemaining = (deletionDate?: string) => {
  if (!deletionDate) return null;
  const elapsedDays =
    (Date.now() - new Date(deletionDate).getTime()) / MS_PER_DAY;
  return Math.max(0, Math.ceil(TTL_DAYS - elapsedDays));
};

// The closure record carries no personal data of its own. Identity is joined in
// server-side from the identity vault and comes back null once it has been
// erased on request, which is what the "Identity erased" state renders.
interface ClosureIdentity {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
}

interface ClosedAccount {
  _id: string;
  userRef: string;
  closureType: "SELF_DELETE" | "ADMIN_PERMANENT_DELETE" | "MERGED_INTO";
  identity?: ClosureIdentity | null;
  actorRef?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  } | null;
  reason?: string | null;
  balanceAtClosureCents?: number;
  droppiesAtClosure?: number;
  accountCreatedAt?: string | null;
  closedAt: string;
  identityErasedAt?: string | null;
  retainUntil: string;
}

const CLOSURE_LABELS: Record<ClosedAccount["closureType"], string> = {
  SELF_DELETE: "Deleted by user",
  ADMIN_PERMANENT_DELETE: "Removed by admin",
  MERGED_INTO: "Merged into another account",
};

const formatEuro = (cents?: number) =>
  new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(Number(cents ?? 0) / 100);

const closureName = (closure: ClosedAccount) => {
  if (!closure.identity) return "Identity erased";
  return (
    [closure.identity.first_name, closure.identity.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "Unnamed user"
  );
};

const closureContact = (closure: ClosedAccount) => {
  if (!closure.identity) return "-";
  return closure.identity.email ?? closure.identity.phone_number ?? "-";
};

function PendingDeletionsScreen() {
  /** Hooks */
  const {
    pendingDeletionUsers,
    pendingDeletionLoading,
    hasNextPendingDeletionPage,
    getPendingDeletionUsers,
  } = useUsers();
  const { setAlert } = useAlert();
  const { setIsLoading } = useAppLoading();

  /** Closed-account history */
  const [closed, setClosed] = useState<ClosedAccount[]>([]);
  const [closedPage, setClosedPage] = useState(1);
  const [closedHasNext, setClosedHasNext] = useState(false);
  const [closedLoading, setClosedLoading] = useState(false);
  const [closedSearch, setClosedSearch] = useState("");
  const [closedType, setClosedType] = useState("");
  const closedDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchClosed = async (
    page: number,
    search: string,
    closureType: string
  ) => {
    setClosedLoading(true);
    try {
      const response = await axios.get(
        `users/closed-accounts?page=${page}&limit=${CLOSED_PAGE_SIZE}` +
          `&search=${encodeURIComponent(search.trim())}` +
          `&closureType=${encodeURIComponent(closureType)}`
      );
      setClosed(response?.data?.closures ?? []);
      setClosedHasNext(Boolean(response?.data?.hasNextPage));
    } catch (error) {
      setClosed([]);
      setClosedHasNext(false);
    } finally {
      setClosedLoading(false);
    }
  };

  /** Effects */
  useEffect(() => {
    getPendingDeletionUsers(1);
  }, []);

  useEffect(() => {
    fetchClosed(closedPage, closedSearch, closedType);
  }, [closedPage]);

  // A new search or filter always restarts at page 1. Debounced so typing does
  // not fire a request per keystroke.
  useEffect(() => {
    if (closedDebounce.current) clearTimeout(closedDebounce.current);
    closedDebounce.current = setTimeout(() => {
      if (closedPage === 1) fetchClosed(1, closedSearch, closedType);
      else setClosedPage(1);
    }, 350);
    return () => {
      if (closedDebounce.current) clearTimeout(closedDebounce.current);
    };
  }, [closedSearch, closedType]);

  // Art. 17 erasure. Removes the stored name, email and phone. The closure
  // record and the balance figure stay, because we are required to keep the
  // financial record for 7 years and it carries no personal data without them.
  const onEraseIdentity = async (closure: ClosedAccount) => {
    const confirmText =
      `Erase the stored name, email and phone number of "${closureName(closure)}"?\n\n` +
      "The closure record and the balance at closure stay, since we are required " +
      "to keep the financial record for 7 years. They will no longer carry a name.\n\n" +
      "This cannot be undone.";
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmText)) return;

    setIsLoading(true);
    try {
      await axios.delete(`users/stored-identity/${closure.userRef}`);
      setAlert({
        isOpen: true,
        message: "Identity erased. The financial record was kept.",
        severity: "success",
      });
      fetchClosed(closedPage, closedSearch, closedType);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ??
          "Could not erase the identity. Please check the logs.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h1 className="font-satoshi text-2xl font-bold text-dark mb-2">
        Pending Deletions
      </h1>
      <p className="text-sm text-gray-text mb-8 max-w-3xl">
        Accounts still inside the {TTL_DAYS} day recovery window, and below them
        the history of accounts that have already left. Closed-account records
        hold no name, email or phone of their own: those are stored separately
        and can be erased on request without losing the financial record.
      </p>

      <div className="w-full px-7 py-7 bg-white rounded-xl shadow-none drop-shadow-none">
        <h2 className="font-satoshi text-lg font-bold text-dark mb-4">
          In the recovery window
        </h2>
        {!pendingDeletionUsers?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            {pendingDeletionLoading
              ? "Loading..."
              : "No accounts are currently pending deletion."}
          </p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
              <thead className="font-medium rounded-xl bg-gray-50">
                <tr>
                  <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3">
                    Full name
                  </th>
                  <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3">
                    Email
                  </th>
                  <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3">
                    Balance
                  </th>
                  <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3">
                    Deletion requested
                  </th>
                  <th scope="col" className="px-2 sm:px-6 py-1 sm:py-3">
                    Days left to recover
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingDeletionUsers?.map((user) => (
                  <tr key={user?._id} className="bg-white border-b">
                    <th
                      scope="row"
                      className="px-2 sm:px-6 py-1 sm:py-3 font-bold text-primary-base whitespace-nowrap"
                    >
                      {user?.first_name} {user?.last_name}
                    </th>
                    <td className="px-2 sm:px-6 py-1 sm:py-3">
                      {user?.email}
                    </td>
                    <td className="px-2 sm:px-6 py-1 sm:py-3">
                      {(Number(user?.balance ?? 0) / 100).toFixed(2)}€
                    </td>
                    <td className="px-2 sm:px-6 py-1 sm:py-3">
                      {user?.deletion_date
                        ? new Date(user.deletion_date).toLocaleDateString()
                        : "-"}
                    </td>
                    <td className="px-2 sm:px-6 py-1 sm:py-3">
                      {daysRemaining(user?.deletion_date) ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasNextPendingDeletionPage && (
          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={() =>
                getPendingDeletionUsers(
                  Math.ceil(pendingDeletionUsers.length / 20) + 1
                )
              }
              className="px-4 py-2 rounded-md font-satoshi font-bold text-xs text-white bg-primary-base hover:opacity-90"
            >
              Load more
            </button>
          </div>
        )}
      </div>

      <div className="w-full px-7 py-7 bg-white rounded-xl shadow-none drop-shadow-none mt-6">
        <h2 className="font-satoshi text-lg font-bold text-dark mb-1">
          Closed accounts
        </h2>
        <p className="text-sm text-gray-text mb-4">
          Every account that has left the system, newest first. Kept for 7 years
          from closure under Dutch tax law, then deleted automatically.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="max-w-sm w-full">
            <SearchInput
              value={closedSearch}
              onChange={(e) => setClosedSearch(e.target.value)}
              placeholder="Search by name, email, or phone number (e.g. +31612345678)"
              hideIcon
            />
          </div>
          <div className="min-w-[220px]">
            <FormSelect
              value={closedType}
              onChange={(e: any) => setClosedType(e.target.value)}
              options={[
                { value: "", label: "All closure types" },
                { value: "SELF_DELETE", label: CLOSURE_LABELS.SELF_DELETE },
                {
                  value: "ADMIN_PERMANENT_DELETE",
                  label: CLOSURE_LABELS.ADMIN_PERMANENT_DELETE,
                },
                { value: "MERGED_INTO", label: CLOSURE_LABELS.MERGED_INTO },
              ]}
              placeholder="All closure types"
            />
          </div>
        </div>

        {closedLoading && !closed.length ? (
          <p className="text-sm text-gray-text">Loading...</p>
        ) : !closed.length ? (
          <p className="text-sm text-gray-text">
            {closedSearch.trim() || closedType
              ? "No closed accounts match this filter."
              : "No accounts have been closed yet."}
          </p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
              <thead className="font-medium bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">Closed</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">Account</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">How</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">By</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">Reason</th>
                  <th
                    className="px-2 sm:px-4 py-2 sm:py-3"
                    title="Balance on the account at the moment it closed"
                  >
                    Balance at closure
                  </th>
                  <th
                    className="px-2 sm:px-4 py-2 sm:py-3 text-right"
                    title="Art. 17 erasure request from the account holder"
                  >
                    Erasure
                  </th>
                </tr>
              </thead>
              <tbody>
                {closed.map((closure) => (
                  <tr
                    key={closure._id}
                    className="bg-white hover:bg-gray-100 transition ease-linear border-b"
                  >
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      {closure.closedAt
                        ? dayjs(closure.closedAt).format("DD/MM/YYYY HH:mm")
                        : "-"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <span className="block font-medium">
                        {closureName(closure)}
                      </span>
                      <span className="block text-xs text-gray-text break-all">
                        {closureContact(closure)}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <span className="inline-flex items-center rounded-full text-xs font-medium px-2 py-0.5 bg-gray-100 text-dark">
                        {CLOSURE_LABELS[closure.closureType] ??
                          closure.closureType}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap">
                      {closure.actorRef
                        ? [
                            closure.actorRef.first_name,
                            closure.actorRef.last_name,
                          ]
                            .filter(Boolean)
                            .join(" ")
                            .trim() ||
                          closure.actorRef.email ||
                          "-"
                        : "The user"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 max-w-[220px]">
                      <span className="text-sm text-gray-text">
                        {closure.reason || "-"}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 whitespace-nowrap font-medium">
                      {formatEuro(closure.balanceAtClosureCents)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-right whitespace-nowrap">
                      {closure.identityErasedAt ? (
                        <span className="text-xs text-gray-text">
                          Erased{" "}
                          {dayjs(closure.identityErasedAt).format("DD/MM/YYYY")}
                        </span>
                      ) : closure.identity ? (
                        <PrimaryButton
                          label="Erase identity"
                          size="sm"
                          onClick={() => onEraseIdentity(closure)}
                          className="!bg-white !border-red-base hover:!bg-red-base hover:!opacity-100 group"
                          textClassName="!text-red-base group-hover:!text-white"
                        />
                      ) : (
                        <span className="text-xs text-gray-text">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-gray-500">Page {closedPage}</p>
          <div className="flex gap-2">
            <PrimaryButton
              label="Previous"
              size="sm"
              onClick={() => setClosedPage((p) => Math.max(1, p - 1))}
              disabled={closedPage <= 1 || closedLoading}
              className="!bg-white !text-dark !border-gray-border disabled:opacity-40"
            />
            <PrimaryButton
              label="Next"
              size="sm"
              onClick={() => setClosedPage((p) => p + 1)}
              disabled={!closedHasNext || closedLoading}
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark disabled:opacity-40"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(
  withSuperAdminPermission(PendingDeletionsScreen)
);
